import type { TravelArticle, TravelImage } from "../../types";

// Photos live in src/assets/travel/anini/ — metadata-stripped copies (no GPS), because
// Parcel keeps EXIF in its output. Parcel resizes them and converts them to WebP at
// build time (the ?as=webp&width=… query). Paths must stay string literals inside
// new URL() so Parcel can find them.
const responsive = (
  sm: string,
  lg: string,
  widths: [number, number],
): TravelImage => ({
  src: sm,
  srcSet: `${sm} ${widths[0]}w, ${lg} ${widths[1]}w`,
});

const town = responsive(
  new URL(
    "../../assets/travel/anini/town.jpg?as=webp&width=900",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/anini/town.jpg?as=webp&width=1800",
    import.meta.url,
  ).href,
  [900, 1800],
);
const valley = responsive(
  new URL(
    "../../assets/travel/anini/valley.jpg?as=webp&width=900&quality=72",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/anini/valley.jpg?as=webp&width=1800&quality=72",
    import.meta.url,
  ).href,
  [900, 1800],
);
const lunch = responsive(
  new URL(
    "../../assets/travel/anini/lunch.jpg?as=webp&width=700",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/anini/lunch.jpg?as=webp&width=1400",
    import.meta.url,
  ).href,
  [700, 1400],
);
const armsOpen = responsive(
  new URL(
    "../../assets/travel/anini/arms-open.jpg?as=webp&width=900&quality=72",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/anini/arms-open.jpg?as=webp&width=1800&quality=72",
    import.meta.url,
  ).href,
  [900, 1800],
);

const anini: TravelArticle = {
  slug: "anini",
  place: "Anini",
  country: "India",
  coord: "Dibang Valley · 28.8°N · 95.9°E",
  date: "October 2023",
  hero: town,
  title: "Anini - A Hidden Blue Zone?",
  body: [
    {
      t: "p",
      drop: true,
      text: "Tucked away in the remote region of Dibang Valley, the beautiful town of Anini was calling to me and my travel mentor (and brother) Aloke da for years. Even though we had travelled extensively in Arunachal Pradesh, we had not ventured into the extreme northeast corners of the state. I was thrilled to explore the Dibang Wildlife Sanctuary while my brother was curious about Anini.",
    },
    {
      t: "p",
      text: "This picturesque town is situated on a plateau between the Dri and Mathu rivers. With a small population in the thousands, the majority of the residents belong to the Idu Mishmi tribe. There are dozens of scattered villages in and around this town. Anini also happens to be the base town for the famous seven lakes trek.",
    },
    {
      t: "p",
      text: "We stayed at the Kongo Homestay for a few days, while we explored the valley with Taing ji. Each day, we hit the road with no destination planned, just soaking in the beauty. Be it the beautiful mountain road of Dibang Wildlife Sanctuary or the majestic Mathu waterfall, every corner of this place is pristine, almost dream-like.",
    },
    {
      t: "photo",
      ...valley,
      alt: "A river winding through steep green hills under a cloudy sky",
      caption: "Somewhere along the valley roads, with no destination planned",
    },
    {
      t: "p",
      text: "I had learned about blue zones a couple of years ago while reading Ikigai. Anini felt nothing short of what it describes. For the uninitiated, a blue zone is a designated region where there is a high concentration of centenarians living and thriving. The noteworthy features of a blue zone include consumption of locally grown whole foods and a strong sense of community. Many instances during our stay pointed to these characteristics.",
    },
    {
      t: "p",
      text: "The whole foods part of that equation showed up almost immediately. One day, I ate a home cooked meal, freshly prepared using garden vegetables at Taing ji’s house. We had gone unannounced but the hospitality was remarkable. That lunch was one of the most satisfying meals of my life.",
    },
    {
      t: "photo",
      portrait: true,
      ...lunch,
      alt: "A plate of rice and cooked greens with king chilli on a woven bamboo floor, hills visible through the window",
      caption: "Lunch at Taing ji’s: rice, greens and king chilli",
    },
    {
      t: "p",
      text: "The community part showed up a little later, and it hit harder. One evening, as we were having tea with the owners of our homestay, Taing ji came running with the news that an elderly lady had gone missing from their village and no one had been able to track her down. They immediately joined him in the search. No questions asked. The entire town was searching for this one person (they did find her later on). Never in my life had I encountered such a place where people not only claimed to be there for each other but showed up in times of need. That’s how strong communities get built and that’s how life becomes more meaningful.",
    },
    { t: "quote", text: "That’s how strong communities get built." },
    { t: "divider" },
    {
      t: "photo",
      ...armsOpen,
      alt: "Megholova on a hillside road with arms outstretched, clouds rolling over the mountains behind",
      caption: "Dibang Valley, soaking it all in",
    },
    {
      t: "p",
      text: "The beauty of this valley awakened the romanticist in me while the people brought hope in my heart — a hope that all of humanity is not doomed; a hope that there are still many unseen remote corners of the world where, quietly, these deeply connected communities are building a beautiful legacy.",
    },
  ],
};

export default anini;
